/**
 * Product form: intercepts the standard `{% form 'product' %}` submit and POSTs to /cart/add.js instead of reloading, dispatching CartAddEvent so the cart drawer opens.
 * @see https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-add-js
 */

import { Component } from "@theme/component";
import { CartAddEvent, CartErrorEvent } from "@theme/events";
import { cartSectionIds } from "@theme/utilities";

class ProductFormComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    const form = this.querySelector("form");
    if (form) form.addEventListener("submit", this.#handleSubmit);
  }

  /**
   * @param {SubmitEvent} event
   */
  #handleSubmit = async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.currentTarget);
    const submitter = /** @type {HTMLButtonElement | null} */ (event.submitter);

    // Don't intercept the dynamic checkout button (Shop Pay, etc.) - let it
    // submit normally so the customer goes straight to checkout.
    if (submitter?.name === "checkout") return;

    event.preventDefault();
    submitter?.setAttribute("disabled", "");

    try {
      const formData = new FormData(form);
      // The drawer HTML comes back in the same response: no second request to refresh it.
      const sections = cartSectionIds();
      if (sections.length) {
        formData.append("sections", sections.join(","));
        formData.append("sections_url", window.location.pathname);
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: { Accept: "application/javascript" },
        body: formData,
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(body.description || body.message || `HTTP ${response.status}`);
      }

      // Tell the rest of the page that the cart changed.
      document.dispatchEvent(new CartAddEvent(body, "product-form", { source: "product-form", sections: body.sections }));
    } catch (error) {
      console.error("[product-form] add-to-cart failed:", error);
      document.dispatchEvent(new CartErrorEvent("product-form", error.message));
    } finally {
      submitter?.removeAttribute("disabled");
    }
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
